import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import materioMultiSelectStyles from './materioSelectStyles';

/**
 * Multi-select picker for Assets. Stores asset IDs in `value`.
 * On open and on text change, fetches a (debounced) page of assets from `asset/all`
 * scoped to the user's organization.
 */
const AssetMultiPicker = ({ value = [], onChange, organizationId, disabled }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  // Remembers every option we've ever fetched, so a selected asset stays
  // selected even after the options list is replaced by a new search.
  const optionCacheRef = useRef(new Map());

  const fetchAssets = async (q = '') => {
    setLoading(true);
    try {
      const params = { count: 50, hierarchy: false };
      if (organizationId) params.organizationId = organizationId;
      if (q && q.trim()) params.search = q.trim();
      const { data } = await api.get('asset/all', { params });
      // Backend returns { response: [{ assets: [...], ... }] } — same shape as MeterForm.
      const raw = data?.response;
      const assets = Array.isArray(raw)
        ? (raw[0]?.assets || raw)
        : (raw?.assets || raw?.content || []);
      setOptions(assets.map((a) => ({
        value: a.id,
        label: `${a.assetName || a.name || ''}${a.serialNumber ? ` · ${a.serialNumber}` : ''}${a.assetNumber ? ` · ${a.assetNumber}` : ''}`,
        raw: a,
      })));
    } catch (e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const handleInput = (input) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAssets(input), 300);
  };

  const selected = useMemo(() => {
    // Merge the latest fetch into the cache, then resolve every selected id
    // against it — selections persist even when they're no longer in `options`.
    const cache = optionCacheRef.current;
    options.forEach((o) => cache.set(o.value, o));
    return (value || []).map((id) => cache.get(id) || { value: id, label: `#${id}` });
  }, [options, value]);

  return (
    <Select
      isMulti
      isDisabled={disabled}
      isLoading={loading}
      options={options}
      value={selected}
      onInputChange={handleInput}
      onChange={(opts) => onChange((opts || []).map((o) => o.value))}
      classNamePrefix="react-select"
      styles={materioMultiSelectStyles}
      placeholder={t('compliance.materiovigilance.field.linked_assets_placeholder', 'Type to search assets...')}
      noOptionsMessage={() => loading
        ? t('shared.loading', 'Loading…')
        : t('compliance.materiovigilance.no_assets', 'No assets found')}
      filterOption={null}
    />
  );
};

export default AssetMultiPicker;
